<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import QaDashboardApi from 'dashboard/api/qaDashboard';

// QA_CLOSE_TYPE_SERVER_AUDIT_V1 — uses close_type_audits API (no ConversationApi resolved loops)
const { t, locale } = useI18n();
const showAlert = useAlert;
const store = useStore();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const isTemplateLoading = ref(false);
const isTemplateSaving = ref(false);
const selectedAgentId = ref(null);
const selectedTemplateCloseType = ref('complete');
const TEMPLATE_LOCALES = ['th', 'en'];
const parseSupportedLocale = rawLocale => {
  const localeText = String(rawLocale || '')
    .trim()
    .toLowerCase()
    .replace('-', '_');
  if (localeText.startsWith('th')) return 'th';
  if (localeText.startsWith('en')) return 'en';
  return null;
};
const normalizeTemplateLocale = rawLocale =>
  parseSupportedLocale(rawLocale) || 'th';
const normalizeTemplateLocaleKey = rawLocale =>
  String(rawLocale || '')
    .trim()
    .toLowerCase()
    .replace('-', '_') || 'default';
const detectTemplateLocaleByText = templateText => {
  const normalizedText = String(templateText || '').trim();
  if (!normalizedText) return null;
  return /[\u0E00-\u0E7F]/.test(normalizedText) ? 'th' : 'en';
};
const selectedTemplateLocale = ref(normalizeTemplateLocale(locale.value));
const closeTypeTemplateInput = ref('');
const closeTypeTemplates = ref({});
const rows = ref([]);
const remarkByConversation = ref({});
const feedbackByConversation = ref({});

const normalize = text => String(text || '').trim();

const agents = computed(() => store.getters['agents/getAgents'] || []);
const selectedAgent = computed(() =>
  agents.value.find(agent => Number(agent.id) === Number(selectedAgentId.value))
);

const closeTypeLabelMap = {
  complete: 'QA_DASHBOARD.CLOSE_TYPES.COMPLETE',
  no_response: 'QA_DASHBOARD.CLOSE_TYPES.NO_RESPONSE',
  promo_complaint: 'QA_DASHBOARD.CLOSE_TYPES.PROMO_COMPLAINT',
  contact_other_party: 'QA_DASHBOARD.CLOSE_TYPES.CONTACT_OTHER_PARTY',
};

const getCloseTypeLabel = closeType => {
  const key = closeTypeLabelMap[String(closeType || '').trim()];
  return key ? t(key) : t('QA_DASHBOARD.CLOSE_TYPES.UNKNOWN');
};

const closeTypeOptions = computed(() =>
  Object.keys(closeTypeLabelMap).map(closeType => ({
    key: closeType,
    label: getCloseTypeLabel(closeType),
  }))
);

const templateLocaleOptions = computed(() => [
  {
    key: 'th',
    label: t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_LANGUAGE_TH'),
  },
  {
    key: 'en',
    label: t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_LANGUAGE_EN'),
  },
]);

const selectedTemplateLocaleLabel = computed(
  () =>
    templateLocaleOptions.value.find(
      option => option.key === selectedTemplateLocale.value
    )?.label || selectedTemplateLocale.value.toUpperCase()
);

const resolveTemplateValueForEditor = (localeTemplates, templateLocale) => {
  if (!localeTemplates || typeof localeTemplates !== 'object') return '';

  const localeKey = normalizeTemplateLocale(templateLocale);
  const localizedTemplate = String(localeTemplates[localeKey] || '');
  if (normalize(localizedTemplate)) return localizedTemplate;

  const hasAnyLocalizedTemplate = TEMPLATE_LOCALES.some(localeOption =>
    normalize(localeTemplates[localeOption])
  );
  const defaultTemplate = String(localeTemplates.default || '');
  const inferredDefaultLocale = detectTemplateLocaleByText(defaultTemplate);
  if (
    normalize(defaultTemplate) &&
    (!hasAnyLocalizedTemplate || inferredDefaultLocale === localeKey)
  ) {
    return defaultTemplate;
  }
  return '';
};

const getLocaleTemplateValue = (closeType, templateLocale) => {
  const closeTypeKey = String(closeType || '');
  const localeTemplates = closeTypeTemplates.value[closeTypeKey];
  if (
    !closeTypeKey ||
    !localeTemplates ||
    typeof localeTemplates !== 'object'
  ) {
    return '';
  }
  return resolveTemplateValueForEditor(localeTemplates, templateLocale);
};

const syncTemplateInput = () => {
  closeTypeTemplateInput.value = getLocaleTemplateValue(
    selectedTemplateCloseType.value,
    selectedTemplateLocale.value
  );
};

const configuredTemplateCount = computed(
  () =>
    Object.values(closeTypeTemplates.value).filter(localeTemplates =>
      normalize(
        resolveTemplateValueForEditor(
          localeTemplates,
          selectedTemplateLocale.value
        )
      )
    ).length
);

const templateOverviewRows = computed(() =>
  closeTypeOptions.value.map(option => ({
    key: option.key,
    label: option.label,
    th: getLocaleTemplateValue(option.key, 'th'),
    en: getLocaleTemplateValue(option.key, 'en'),
  }))
);

watch(
  [
    selectedTemplateCloseType,
    selectedTemplateLocale,
    () => closeTypeTemplates.value,
  ],
  syncTemplateInput,
  { immediate: true, deep: true }
);

const stats = computed(() => {
  const total = rows.value.length;
  const match = rows.value.filter(row => row.scoreStatus === 'match').length;
  const mismatch = rows.value.filter(
    row => row.scoreStatus === 'mismatch'
  ).length;
  const unscored = rows.value.filter(
    row => row.scoreStatus === 'unscored'
  ).length;
  return {
    total,
    match,
    mismatch,
    unscored,
  };
});

const scoreCloseTypeComparison = (actual, predicted) => {
  const actualKey = normalize(actual).toLowerCase();
  if (!actualKey || actualKey === 'unknown' || !predicted) return 'unscored';
  return actualKey === String(predicted).toLowerCase() ? 'match' : 'mismatch';
};

const heuristicCloseType = conversation => {
  const text = String(
    conversation.last_non_activity_message?.content ||
      conversation.messages?.[0]?.content ||
      ''
  ).toLowerCase();

  if (!text) return null;
  if (/(promo|promotion|complain|complaint|scam|fraud)/i.test(text)) {
    return 'promo_complaint';
  }
  if (/(contact other party|third party|contact partner)/i.test(text)) {
    return 'contact_other_party';
  }
  if (/(no response|no reply|unreachable|no answer)/i.test(text)) {
    return 'no_response';
  }
  if (/(done|solved|resolved|thank you|thanks|ok)/i.test(text)) {
    return 'complete';
  }
  return null;
};

const templateStats = computed(() => ({
  mismatch: rows.value.filter(row => row.templateStatus === 'mismatch').length,
  match: rows.value.filter(row => row.templateStatus === 'match').length,
}));

const ensureAgentsLoaded = async () => {
  if (agents.value.length) return;
  await store.dispatch('agents/get');
};

const loadCloseTypeTemplates = async () => {
  isTemplateLoading.value = true;
  try {
    const response = await QaDashboardApi.getCloseTypeTemplates(
      selectedTemplateLocale.value
    );
    const items = response?.data?.items || [];
    closeTypeTemplates.value = items.reduce((accumulator, item) => {
      const closeType = String(item.close_type || '');
      if (closeType) {
        const rawTemplateTexts =
          item.template_texts && typeof item.template_texts === 'object'
            ? item.template_texts
            : {};
        const normalizedTemplateTexts = Object.entries(rawTemplateTexts).reduce(
          (templateAccumulator, [rawLocale, templateText]) => {
            const localeKey = normalizeTemplateLocaleKey(rawLocale);
            templateAccumulator[localeKey] = String(templateText || '');
            return templateAccumulator;
          },
          {}
        );
        if (normalize(item.template_text)) {
          normalizedTemplateTexts[selectedTemplateLocale.value] = String(
            item.template_text
          );
        }
        accumulator[closeType] = normalizedTemplateTexts;
      }
      return accumulator;
    }, {});
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_LOAD_TEMPLATE')
    );
  } finally {
    isTemplateLoading.value = false;
  }
};

const saveCloseTypeTemplate = async () => {
  if (!selectedTemplateCloseType.value) return;

  isTemplateSaving.value = true;
  try {
    await QaDashboardApi.updateCloseTypeTemplate({
      close_type: selectedTemplateCloseType.value,
      locale: selectedTemplateLocale.value,
      template_text: closeTypeTemplateInput.value.toString(),
    });
    await loadCloseTypeTemplates();
    showAlert(t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.SAVE_TEMPLATE_SUCCESS'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_SAVE_TEMPLATE')
    );
  } finally {
    isTemplateSaving.value = false;
  }
};

const fetchCloseTypeAuditRows = async agentId => {
  // QA_CLOSE_TYPE_SERVER_AUDIT_V1
  const all = [];
  let page = 1;
  const perPage = 50;
  const maxPages = 100;

  while (page <= maxPages) {
    const response = await QaDashboardApi.getCloseTypeAudits({
      agent_id: agentId,
      page,
      per_page: perPage,
    });
    const items = response?.data?.items || [];
    all.push(...items);
    const totalPages = Number(response?.data?.meta?.total_pages || 1);
    if (!items.length || page >= totalPages) break;
    page += 1;
  }

  return all;
};

const mapAuditItemToRow = item => {
  const scoreStatus = item.score_status || 'unscored';
  return {
    id: item.id,
    actual: item.actual || null,
    predicted: item.predicted || null,
    scoreStatus,
    isMismatch: scoreStatus === 'mismatch',
    expectedTemplate: normalize(item.expected_template)
      ? item.expected_template
      : '-',
    agentReply: normalize(item.agent_reply) ? item.agent_reply : '-',
    templateStatus: item.template_status || 'not_configured',
    conversationRoute: accountScopedRoute('inbox_conversation', {
      conversation_id: item.id,
    }),
  };
};

const getMessageSenderName = message =>
  String(
    message?.sender_name ||
      message?.sender?.name ||
      message?.sender?.available_name ||
      ''
  );

const isOutgoingBySelectedAgent = message => {
  if (!message || Number(message.message_type) !== 1) return false;

  const selectedAgentIdNumber = Number(selectedAgent.value?.id);
  const senderId = Number(message.sender_id || message.sender?.id || 0);
  if (selectedAgentIdNumber && senderId) {
    return selectedAgentIdNumber === senderId;
  }

  return (
    normalize(getMessageSenderName(message)).toLowerCase() ===
    normalize(selectedAgent.value?.name).toLowerCase()
  );
};

const extractAgentReply = conversation => {
  const candidateMessages = [
    conversation.last_non_activity_message,
    ...(conversation.messages || []),
  ].filter(Boolean);

  const agentMessage = candidateMessages.find(isOutgoingBySelectedAgent);
  if (agentMessage) return String(agentMessage.content || '');

  const fallbackOutgoingMessage = candidateMessages.find(
    message => Number(message.message_type) === 1
  );
  return String(fallbackOutgoingMessage?.content || '');
};

const resolveConversationTemplateLocale = conversation => {
  const localeCandidates = [
    conversation.custom_attributes?.language,
    conversation.custom_attributes?.detected_language,
    conversation.custom_attributes?.locale,
    conversation.additional_attributes?.language,
    conversation.additional_attributes?.detected_language,
    conversation.additional_attributes?.locale,
    conversation.additional_attributes?.browser_language,
  ];

  const attributeLocale = localeCandidates
    .map(parseSupportedLocale)
    .find(Boolean);
  if (attributeLocale) return attributeLocale;

  const customerText = String(
    conversation.last_non_activity_message?.content ||
      conversation.messages?.[0]?.content ||
      ''
  );
  if (normalize(customerText)) {
    return /[\u0E00-\u0E7F]/.test(customerText) ? 'th' : 'en';
  }

  return normalizeTemplateLocale(selectedTemplateLocale.value);
};

const resolveTemplateTextByLocale = (closeType, preferredLocale) => {
  const localeTemplates = closeTypeTemplates.value[String(closeType)] || {};
  if (!localeTemplates || typeof localeTemplates !== 'object') return '';

  const requestedLocale = parseSupportedLocale(preferredLocale);
  const requestedTemplate = requestedLocale
    ? String(localeTemplates[requestedLocale] || '')
    : '';
  if (normalize(requestedTemplate)) {
    return requestedTemplate;
  }

  const defaultTemplate = String(localeTemplates.default || '');
  const inferredDefaultLocale = detectTemplateLocaleByText(defaultTemplate);
  if (
    normalize(defaultTemplate) &&
    (!requestedLocale || inferredDefaultLocale === requestedLocale)
  ) {
    return defaultTemplate;
  }

  const fallbackLocale =
    requestedLocale === 'th' ? 'en' : requestedLocale === 'en' ? 'th' : null;
  const fallbackTemplate = fallbackLocale
    ? String(localeTemplates[fallbackLocale] || '')
    : '';

  return (
    (normalize(fallbackTemplate) && fallbackTemplate) ||
    localeTemplates.th ||
    localeTemplates.en ||
    defaultTemplate ||
    ''
  ).toString();
};

const resolveExpectedTemplate = (closeType, preferredLocale) => {
  const rawTemplate = resolveTemplateTextByLocale(closeType, preferredLocale);
  if (!normalize(rawTemplate)) return '';
  const agentName = selectedAgent.value?.name || '';
  return rawTemplate.toString().replaceAll('{agent_name}', agentName);
};

const getTemplateStatus = ({ closeType, agentReply, templateLocale }) => {
  const expectedTemplate = resolveExpectedTemplate(closeType, templateLocale);
  if (!normalize(expectedTemplate)) return 'not_configured';
  if (!normalize(agentReply)) return 'no_agent_reply';

  return normalize(agentReply) === normalize(expectedTemplate)
    ? 'match'
    : 'mismatch';
};

const getTemplateStatusLabel = status => {
  if (status === 'match') {
    return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_MATCH');
  }
  if (status === 'mismatch') {
    return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_MISMATCH');
  }
  if (status === 'no_agent_reply') {
    return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_NO_AGENT_REPLY');
  }
  return t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_NOT_CONFIGURED');
};

const getTemplateStatusClass = status => {
  if (status === 'match') return 'text-n-teal-11';
  if (status === 'mismatch') return 'text-n-ruby-11';
  return 'text-n-slate-11';
};

const runAudit = async () => {
  if (!selectedAgent.value) return;

  isLoading.value = true;
  rows.value = [];
  remarkByConversation.value = {};
  feedbackByConversation.value = {};
  try {
    const items = await fetchCloseTypeAuditRows(selectedAgent.value.id);
    rows.value = items.map(mapAuditItemToRow);
    await loadPersistedReviews(rows.value.map(row => row.id));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_RUN_AUDIT')
    );
  } finally {
    isLoading.value = false;
  }
};

const setFeedback = async (conversationId, feedback) => {
  feedbackByConversation.value = {
    ...feedbackByConversation.value,
    [conversationId]: feedback,
  };
  await persistReview(conversationId, { feedback });
};

const persistReview = async (conversationId, overrides = {}) => {
  const row = rows.value.find(
    item => Number(item.id) === Number(conversationId)
  );
  try {
    await QaDashboardApi.upsertCloseTypeReview({
      conversation_id: conversationId,
      remark: remarkByConversation.value[conversationId] || '',
      feedback: feedbackByConversation.value[conversationId] || '',
      actual_close_type: row?.actual,
      predicted_close_type: row?.predicted,
      template_status: row?.templateStatus,
      ...overrides,
    });
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ERROR_RUN_AUDIT')
    );
  }
};

const onRemarkBlur = conversationId => {
  persistReview(conversationId);
};

const loadPersistedReviews = async conversationIds => {
  if (!conversationIds.length) return;
  try {
    const response = await QaDashboardApi.getCloseTypeReviews({
      conversation_ids: conversationIds,
    });
    const items = response?.data?.items || [];
    const remarks = { ...remarkByConversation.value };
    const feedback = { ...feedbackByConversation.value };
    items.forEach(item => {
      remarks[item.conversation_id] = item.remark || '';
      feedback[item.conversation_id] = item.feedback || '';
    });
    remarkByConversation.value = remarks;
    feedbackByConversation.value = feedback;
  } catch (_error) {
    // Non-blocking: audit table still works without persisted reviews.
  }
};

onMounted(async () => {
  await Promise.all([ensureAgentsLoaded(), loadCloseTypeTemplates()]);
});
</script>

<template>
  <div class="qa-page">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">
            {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TITLE') }}
          </h1>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn qa-btn--primary"
            :disabled="isTemplateSaving || isTemplateLoading"
            @click="saveCloseTypeTemplate"
          >
            {{
              isTemplateSaving
                ? t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.SAVING_TEMPLATE')
                : t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.SAVE_TEMPLATE')
            }}
          </button>
        </div>
      </div>

      <div class="qa-subheader">
        <p class="qa-desc">
          {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.DESCRIPTION') }}
        </p>
      </div>

      <div class="qa-toolbar">
        <div class="qa-toolbar-left" style="flex: 1">
          <label class="qa-field" style="min-width: 240px; flex: 1">
            <span class="qa-label">{{ t('QA_DASHBOARD.FORM.AGENT') }}</span>
            <select v-model="selectedAgentId" class="qa-select">
              <option :value="null">
                {{ t('QA_DASHBOARD.FORM.SELECT_AGENT') }}
              </option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </label>
          <button
            type="button"
            class="qa-btn qa-btn--primary"
            :disabled="isLoading || !selectedAgent"
            @click="runAudit"
          >
            {{
              isLoading
                ? t('QA_DASHBOARD.FORM.RUNNING')
                : t('QA_DASHBOARD.FORM.RUN_AUDIT')
            }}
          </button>
        </div>
      </div>

      <div class="qa-content">
        <div class="qa-kpis">
          <div class="qa-kpi qa-kpi--total">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.STATS.TOTAL') }}
            </div>
            <div class="qa-kpi-value">{{ stats.total }}</div>
          </div>
          <div class="qa-kpi">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.STATS.MATCH') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--good">
              {{ stats.match }}
            </div>
          </div>
          <div class="qa-kpi">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.STATS.MISMATCH') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--bad">
              {{ stats.mismatch }}
            </div>
          </div>
          <div class="qa-kpi">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.UNSCORED') }}
            </div>
            <div class="qa-kpi-value">
              {{ stats.unscored }}
            </div>
          </div>
          <div class="qa-kpi">
            <div class="qa-kpi-label">
              {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_MISMATCH_COUNT') }}
            </div>
            <div class="qa-kpi-value qa-kpi-value--bad">
              {{ templateStats.mismatch }}
              <span class="qa-muted qa-kpi-suffix">
                / {{ templateStats.match + templateStats.mismatch }}
              </span>
            </div>
          </div>
        </div>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 6v12m6-6H6"
                />
              </svg>
              {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_SECTION_TITLE') }}
            </h2>
          </div>

          <div class="qa-card-body">
            <div class="qa-form-grid">
              <div class="qa-field">
                <label class="qa-label">
                  {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_CLOSE_TYPE') }}
                </label>
                <select
                  v-model="selectedTemplateCloseType"
                  class="qa-select"
                  :disabled="isTemplateSaving || isTemplateLoading"
                >
                  <option
                    v-for="option in closeTypeOptions"
                    :key="option.key"
                    :value="option.key"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="qa-field">
                <label class="qa-label">
                  {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_LANGUAGE') }}
                </label>
                <select
                  v-model="selectedTemplateLocale"
                  class="qa-select"
                  :disabled="isTemplateSaving || isTemplateLoading"
                >
                  <option
                    v-for="option in templateLocaleOptions"
                    :key="option.key"
                    :value="option.key"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div class="qa-field qa-field--span-2">
                <label class="qa-label">
                  {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_TEXT') }}
                </label>
                <textarea
                  v-model="closeTypeTemplateInput"
                  rows="3"
                  class="qa-textarea"
                  :placeholder="
                    t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_TEXT_PLACEHOLDER')
                  "
                  :disabled="isTemplateSaving || isTemplateLoading"
                />
              </div>
            </div>

            <p class="qa-hint" style="margin-top: 12px">
              {{
                t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_HINT', {
                  count: configuredTemplateCount,
                  language: selectedTemplateLocaleLabel,
                })
              }}
            </p>
          </div>

          <div class="qa-subcard">
            <div class="qa-subcard-header">
              <div class="qa-subcard-title">
                {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_TITLE') }}
              </div>
              <div class="qa-subcard-desc">
                {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_HINT') }}
              </div>
            </div>

            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">
                      {{
                        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_CLOSE_TYPE')
                      }}
                    </th>
                    <th class="qa-th">
                      {{
                        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_TH')
                      }}
                    </th>
                    <th class="qa-th">
                      {{
                        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_EN')
                      }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isTemplateLoading">
                    <td class="qa-td qa-td--empty" colspan="3">
                      <div class="qa-loading-row">
                        <div class="qa-spinner" />
                        <span>{{
                          t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.LOADING_TEMPLATES')
                        }}</span>
                      </div>
                    </td>
                  </tr>

                  <tr v-for="row in templateOverviewRows" :key="row.key">
                    <td class="qa-td" style="font-weight: 600">
                      {{ row.label }}
                    </td>
                    <td class="qa-td qa-td--pre">
                      {{
                        row.th ||
                        t(
                          'QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_EMPTY'
                        )
                      }}
                    </td>
                    <td class="qa-td qa-td--pre">
                      {{
                        row.en ||
                        t(
                          'QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_OVERVIEW_EMPTY'
                        )
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header">
            <h2 class="qa-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7h13M8 12h13M8 17h13M3 7h.01M3 12h.01M3 17h.01"
                />
              </svg>
              Results
            </h2>
          </div>

          <div class="qa-table-wrap">
            <table class="qa-table">
              <thead>
                <tr>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.TABLE.CONVERSATION') }}
                  </th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.ACTUAL') }}
                  </th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.PREDICTED') }}
                  </th>
                  <th class="qa-th">{{ t('QA_DASHBOARD.TABLE.STATUS') }}</th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.EXPECTED_TEMPLATE') }}
                  </th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.AGENT_REPLY') }}
                  </th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.TEMPLATE_STATUS') }}
                  </th>
                  <th class="qa-th">{{ t('QA_DASHBOARD.TABLE.REMARK') }}</th>
                  <th class="qa-th">
                    {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.FEEDBACK') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td class="qa-td qa-td--empty" colspan="9">
                    <div class="qa-loading-row">
                      <div class="qa-spinner" />
                      <span>{{ t('QA_DASHBOARD.FORM.RUNNING') }}</span>
                    </div>
                  </td>
                </tr>

                <tr v-for="row in rows" :key="row.id">
                  <td class="qa-td">
                    <RouterLink class="qa-link" :to="row.conversationRoute">
                      #{{ row.id }}
                    </RouterLink>
                  </td>
                  <td class="qa-td">{{ getCloseTypeLabel(row.actual) }}</td>
                  <td class="qa-td">
                    {{ getCloseTypeLabel(row.predicted) }}
                  </td>
                  <td class="qa-td">
                    <span
                      class="qa-badge"
                      :class="{
                        'qa-badge--bad': row.scoreStatus === 'mismatch',
                        'qa-badge--good': row.scoreStatus === 'match',
                        'qa-badge--muted': row.scoreStatus === 'unscored',
                      }"
                    >
                      {{
                        row.scoreStatus === 'mismatch'
                          ? t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.MISMATCH')
                          : row.scoreStatus === 'match'
                            ? t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.MATCH')
                            : t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.UNSCORED')
                      }}
                    </span>
                  </td>
                  <td class="qa-td qa-td--pre">
                    {{ row.expectedTemplate }}
                  </td>
                  <td class="qa-td qa-td--pre">{{ row.agentReply }}</td>
                  <td class="qa-td">
                    <span
                      class="qa-badge"
                      :class="{
                        'qa-badge--good': row.templateStatus === 'match',
                        'qa-badge--bad': row.templateStatus === 'mismatch',
                        'qa-badge--muted':
                          row.templateStatus !== 'match' &&
                          row.templateStatus !== 'mismatch',
                      }"
                    >
                      {{ getTemplateStatusLabel(row.templateStatus) }}
                    </span>
                  </td>
                  <td class="qa-td">
                    <input
                      v-model="remarkByConversation[row.id]"
                      class="qa-input qa-inline-input"
                      :placeholder="
                        t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.REMARK_PLACEHOLDER')
                      "
                      @blur="onRemarkBlur(row.id)"
                    />
                  </td>
                  <td class="qa-td">
                    <div class="qa-actions">
                      <button
                        type="button"
                        class="qa-chip"
                        :class="{
                          'qa-chip--active':
                            feedbackByConversation[row.id] === 'ai_correct',
                        }"
                        @click="setFeedback(row.id, 'ai_correct')"
                      >
                        {{
                          t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.HEURISTIC_CORRECT')
                        }}
                      </button>
                      <button
                        type="button"
                        class="qa-chip"
                        :class="{
                          'qa-chip--active':
                            feedbackByConversation[row.id] === 'ai_wrong',
                        }"
                        @click="setFeedback(row.id, 'ai_wrong')"
                      >
                        {{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.HEURISTIC_WRONG') }}
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="!rows.length && !isLoading">
                  <td class="qa-td qa-td--empty" colspan="9">
                    {{ t('QA_DASHBOARD.TABLE.EMPTY') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div class="qa-footer">
        <span>{{ t('QA_DASHBOARD.CLOSE_TYPE_AUDIT.HINT') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../styles/qaShell.css';

.qa-kpi-suffix {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 600;
}
</style>
